import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_THEME_MODE,
  THEME_CHANGE_EVENT,
  THEME_PRIMARY_VAR,
  THEME_SECONDARY_VAR,
  THEME_STORAGE_KEY,
  ThemeMode
} from "constants/theme";
import {
  applyThemeModeToDocument,
  getStoredThemeColors,
  getStoredThemeMode,
  persistThemeMode
} from "utilities/themeColors";

type UseThemeColorPickerReturn = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  primaryColor: string;
  secondaryColor: string;
  themeMode: ThemeMode;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  handleReset: () => void;
  toggleThemeMode: () => void;
  handlePrimaryChange: (color: string) => void;
  handleSecondaryChange: (color: string) => void;
  setPrimaryColor: Dispatch<SetStateAction<string>>;
  setSecondaryColor: Dispatch<SetStateAction<string>>;
  setThemeMode: Dispatch<SetStateAction<ThemeMode>>;
};

const useThemeColorPicker = (): UseThemeColorPickerReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [hasColorsHydrated, setHasColorsHydrated] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>(DEFAULT_THEME_MODE);
  const [hasThemeHydrated, setHasThemeHydrated] = useState(false);
  const latestColorsRef = useRef({ primary: DEFAULT_PRIMARY_COLOR, secondary: DEFAULT_SECONDARY_COLOR });
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedColors = getStoredThemeColors();

    if (storedColors) {
      const { primary, secondary } = storedColors;

      if (primary) {
        setPrimaryColor(primary);
      }

      if (secondary) {
        setSecondaryColor(secondary);
      }

      setHasColorsHydrated(true);
      return;
    }

    const computedStyles = getComputedStyle(document.documentElement);
    const currentPrimary = computedStyles.getPropertyValue(THEME_PRIMARY_VAR).trim() || DEFAULT_PRIMARY_COLOR;
    const currentSecondary = computedStyles.getPropertyValue(THEME_SECONDARY_VAR).trim() || DEFAULT_SECONDARY_COLOR;

    setPrimaryColor(currentPrimary);
    setSecondaryColor(currentSecondary);
    setHasColorsHydrated(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedMode = getStoredThemeMode();
    setThemeMode(storedMode);
    applyThemeModeToDocument(storedMode);
    setHasThemeHydrated(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    document.documentElement.style.setProperty(THEME_PRIMARY_VAR, primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    document.documentElement.style.setProperty(THEME_SECONDARY_VAR, secondaryColor);
  }, [secondaryColor]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!hasColorsHydrated) {
      return;
    }

    window.localStorage.setItem(
      THEME_STORAGE_KEY,
      JSON.stringify({ primary: primaryColor, secondary: secondaryColor })
    );
  }, [primaryColor, secondaryColor, hasColorsHydrated]);

  useEffect(() => {
    latestColorsRef.current = {
      primary: primaryColor,
      secondary: secondaryColor
    };
  }, [primaryColor, secondaryColor]);

  const syncColorsWithCssDefaults = useCallback(() => {
    if (typeof window === "undefined") {
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      setSecondaryColor(DEFAULT_SECONDARY_COLOR);
      return;
    }

    document.documentElement.style.removeProperty(THEME_PRIMARY_VAR);
    document.documentElement.style.removeProperty(THEME_SECONDARY_VAR);

    const computedStyles = getComputedStyle(document.documentElement);
    const cssPrimary = computedStyles.getPropertyValue(THEME_PRIMARY_VAR).trim() || DEFAULT_PRIMARY_COLOR;
    const cssSecondary = computedStyles.getPropertyValue(THEME_SECONDARY_VAR).trim() || DEFAULT_SECONDARY_COLOR;

    setPrimaryColor(cssPrimary);
    setSecondaryColor(cssSecondary);
  }, []);

  const handleReset = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    }

    syncColorsWithCssDefaults();
  };

  useEffect(() => {
    if (!hasThemeHydrated) {
      return;
    }

    applyThemeModeToDocument(themeMode);
    persistThemeMode(themeMode);

    if (typeof window !== "undefined") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    }

    syncColorsWithCssDefaults();
  }, [themeMode, hasThemeHydrated, syncColorsWithCssDefaults]);

  const toggleThemeMode = () => {
    setThemeMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const detail = {
      primaryColor,
      secondaryColor,
      timestamp: Date.now()
    };

    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail }));
  }, [primaryColor, secondaryColor]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      const computedStyles = getComputedStyle(document.documentElement);
      const computedPrimary = computedStyles.getPropertyValue(THEME_PRIMARY_VAR).trim() || DEFAULT_PRIMARY_COLOR;
      const computedSecondary = computedStyles.getPropertyValue(THEME_SECONDARY_VAR).trim() || DEFAULT_SECONDARY_COLOR;
      const { primary: latestPrimary, secondary: latestSecondary } = latestColorsRef.current;

      if (computedPrimary !== latestPrimary) {
        setPrimaryColor(computedPrimary);
      }

      if (computedSecondary !== latestSecondary) {
        setSecondaryColor(computedSecondary);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"]
    });

    return () => observer.disconnect();
  }, []);

  const handlePrimaryChange = (value: string) => {
    setPrimaryColor(value);
  };

  const handleSecondaryChange = (value: string) => {
    setSecondaryColor(value);
  };

  return {
    isOpen,
    setIsOpen,
    primaryColor,
    secondaryColor,
    themeMode,
    panelRef,
    handleReset,
    toggleThemeMode,
    handlePrimaryChange,
    handleSecondaryChange,
    setPrimaryColor,
    setSecondaryColor,
    setThemeMode
  };
};

export default useThemeColorPicker;

